import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { googleLoginUrl } from '../services/authApi';
import GoogleIcon from './icons/GoogleIcon';
import './ProfilePromptModal.css';

export type ProfilePromptMoment = 'before_pay' | 'after_pay' | 'exit_no_purchase';

interface ProfilePromptModalProps {
  moment: ProfilePromptMoment;
  isOpen: boolean;
  onClose: () => void;
  onContinueWithout?: () => void;
}

const DISMISS_KEYS: Record<ProfilePromptMoment, string> = {
  before_pay: 'surf_profile_dismiss_before_pay',
  after_pay: 'surf_profile_dismiss_after_pay',
  exit_no_purchase: 'surf_profile_dismiss_exit',
};

export function wasPromptDismissed(moment: ProfilePromptMoment): boolean {
  return sessionStorage.getItem(DISMISS_KEYS[moment]) === '1';
}

export function dismissPrompt(moment: ProfilePromptMoment): void {
  sessionStorage.setItem(DISMISS_KEYS[moment], '1');
}

const ProfilePromptModal: React.FC<ProfilePromptModalProps> = ({
  moment,
  isOpen,
  onClose,
  onContinueWithout,
}) => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const p = t.profilePrompt;

  if (!isOpen || isAuthenticated) return null;

  const copy =
    moment === 'before_pay'
      ? { title: p.beforePayTitle, body: p.beforePayBody, cta: p.createProfile }
      : moment === 'after_pay'
        ? { title: p.afterPayTitle, body: p.afterPayBody, cta: p.saveToProfile }
        : { title: p.exitTitle, body: p.exitBody, cta: p.saveResults };

  const handleDismiss = () => {
    dismissPrompt(moment);
    onClose();
    onContinueWithout?.();
  };

  return (
    <div className="profile-prompt-overlay" role="dialog" aria-modal="true">
      <div className="profile-prompt-modal">
        <button type="button" className="profile-prompt-close" onClick={handleDismiss} aria-label="Cerrar">
          ×
        </button>
        <h2>{copy.title}</h2>
        <p>{copy.body}</p>
        <div className="profile-prompt-actions">
          <Link to="/cuenta/entrar" className="btn-profile-primary" onClick={() => dismissPrompt(moment)}>
            {copy.cta}
          </Link>
          <a className="btn-profile-oauth" href={googleLoginUrl()}>
            <GoogleIcon size={18} />
            {p.continueGoogle}
          </a>
          <button type="button" className="btn-profile-skip" onClick={handleDismiss}>
            {moment === 'after_pay' ? p.downloadNowOnly : p.notNow}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePromptModal;
